import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useAgencyAuth } from "../../context/AgencyAuthContext";
import Button from "../common/Button";
import Input from "../common/Input";
import Loading from "../common/Loading";
import bgImg from "../../pnai-bg.jpg";
import Logo from "../common/Logo";

const AcceptAdminInvite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { setIsAuthenticated, setAgency } = useAgencyAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agencyName, setAgencyName] = useState("");
  const [invitedEmail, setInvitedEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [errorState, setErrorState] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link");
      setIsLoading(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await axios.get(
          `/api/agency/validate-admin-invite?token=${token}`,
        );
        setAgencyName(response.data.agencyName);
        setInvitedEmail(response.data.email);
        setIsLoading(false);
      } catch (error: any) {
        setError(error.response?.data?.error || "Invalid invitation link");
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      setErrorState(false);

      try {
        const response = await axios.post("/api/agency/accept-admin-invite", {
          token,
          name: name || undefined,
          password,
        });

        setIsAuthenticated(true);
        setAgency(response.data.agency);
        toast.success("Account created successfully!");
        navigate("/agency/dashboard");
      } catch (error: any) {
        setErrorState(true);
        setErrors({
          overall:
            error.response?.data?.error || "Failed to create admin account",
        });
        setIsSubmitting(false);
      }
    } else {
      setErrorState(true);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div
          className="hidden md:flex relative items-center justify-center bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${bgImg})` }}
        >
          <div className="absolute inset-0 bg-blue-50/70 dark:bg-blue-900/70"></div>
          <div className="w-3/5 z-10">
            <Logo className="w-full h-auto" />
          </div>
        </div>
        <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-base-200">
          <div className="bg-base-100 p-8 rounded-xl shadow-md dark:border dark:border-base-300 w-full max-w-sm">
            <h1 className="text-3xl mb-4 font-semibold text-red-600 dark:text-red-400">
              Invalid Invitation
            </h1>
            <p className="text-gray-700 dark:text-base-content/80 mb-6">{error}</p>
            <Button
              onClick={() => navigate("/agency/login")}
              variant="primary"
              className="w-full"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2">
      <div
        className="hidden md:flex relative items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImg})` }}
      >
        <div className="absolute inset-0 bg-blue-50/70 dark:bg-blue-900/70"></div>
        <div className="w-3/5 z-10">
          <Logo className="w-full h-auto" />
        </div>
      </div>
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-base-200">
        <div className="bg-base-100 p-8 rounded-xl shadow-md dark:border dark:border-base-300 w-full max-w-sm">
          <h1 className="text-2xl mb-4 font-semibold">Join as Admin</h1>
          <p className="mb-6 text-sm text-gray-600 dark:text-base-content/70">
            Create your account to become an administrator of{" "}
            <span className="font-semibold text-base-content">{agencyName}</span>
          </p>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <Input
                title="Name"
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="w-full"
              />
            </div>

            <div>
              <Input
                title="Email"
                type="email"
                id="email"
                value={invitedEmail}
                onChange={() => {}}
                disabled
                className="w-full"
              />
            </div>

            <div>
              <Input
                title="Password"
                type="password"
                id="password"
                value={password}
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setPassword(e.target.value);
                }}
                placeholder="Enter your password (min 8 characters)"
                className={`w-full ${errorState && errors.password ? "border-red-500" : ""}`}
              />
              {errors.password && errorState && (
                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
              )}
            </div>

            <div>
              <Input
                title="Confirm Password"
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setConfirmPassword(e.target.value);
                }}
                placeholder="Confirm your password"
                className={`w-full ${errorState && errors.confirmPassword ? "border-red-500" : ""}`}
              />
              {errors.confirmPassword && errorState && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.confirmPassword}
                </p>
              )}
              {errors.overall && errorState && (
                <p className="mt-1 text-xs text-red-500">{errors.overall}</p>
              )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-md p-4">
              <p className="text-xs text-blue-800 dark:text-blue-300 font-semibold">
                What you'll be able to do:
              </p>
              <ul className="mt-2 text-xs text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                <li>Manage officers and invitations</li>
                <li>Configure agency preferences</li>
                <li>Manage checklists and snippets</li>
              </ul>
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Creating Account...
                </>
              ) : (
                "Create Admin Account"
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AcceptAdminInvite;
